import { Paper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { datePickerToShortDate, getDateTime } from './date';
import EntityBulkUpload from './entityBulkUpload';
import EntityCreate from './entityCreate';
import ExportCSV from './exportCSV';
import GroupSelect from './groupSelect';
import { Api, Cosmos, Query } from './interface';
import { BuildGroupQuery } from './searchQuery';
import { IsRole } from './validator';
import { handleResize, bake } from './common';
import ActionContext from './actionContext';
import EntityBulkEdit from './entityBulkEdit.js';
import TransactionRequestCreate from './transactionRequestCreate';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import EntityLocationScan from './entityLocationScan.js';
import PageHeader from './pageHeader.js';
import PageFooter from './pageFooter.js';
import PageTable from './pageTable.js';
import EquipmentService from './equipmentService';
import EquipmentServiceHistory from './equipmentServiceHistory.js';
import { fetchIsoData, fetchIsoFields } from './isoUtil.js';
import { downloadAssetNumberUpdateReport } from './bulkUtil.js';
import FiltersSelected from './filtersSelected.js';
import ColumnFilter from './columnFilter.js';

export default function AdvancedSearch(props) {
  const [currentModal, setCurrentModal] = useState(null);
  const [actionContextAnchor, setActionContextAnchor] = useState(null);
  const [headerContextAnchor, setHeaderContextAnchor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [currentColumn, setCurrentColumn] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [anchorSelection, setAnchorSelection] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [levels, setLevels] = useState([]);
  const [groupedColumns, setGroupedColumns] = useState([]);
  const [currentRowHover, setCurrentRowHover] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [contextRow, setContextRow] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [groupId, setGroupId] = useState(null);
  const [groupLocationFilter, setGroupLocationFilter] = useState(null);
  const [requestOwnerId, setRequestOwnerId] = useState(null);
  const [cartActive, setCartActive] = useState(false);
  const [request, setRequest] = useState({ reserved: [] });
  const [currentEntityType, setCurrentEntityType] = useState(null);
  const [uploaded, setUploaded] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [spinner, setSpinner] = useState(false);
  const [giveIts, setGiveIts] = useState(null);
  const [pageTitle, setPageTitle] = useState('');
  const [isoData, setISOData] = useState(null);
  const [ISOFields, setISOFields] = useState(null);
  const [defaultColumns, setDefaultColumns] = useState([
    { id: 'Name', value: -1, label: 'Name' },
  ]);
  const [archiveMode, setArchiveMode] = useState(false);
  const [searchParams] = useSearchParams();
  const [filterSelected, setFilterSelected] = useState([]);
  const [sortedColumns, setSortedColumns] = useState([]);

  const { locationState } = useOutletContext();

  const [filterCSSClass, setFilterCSSClass] = useState({});

  const filterSelectedRef = useRef(filterSelected);
  const setFilterSelectedRef = (data) => {
    filterSelectedRef.current = data;
    setFilterSelected(data);
  };
  const currentModalRef = useRef(currentModal);
  const setCurrentModalRef = (data) => {
    currentModalRef.current = data;
    setCurrentModal(data);
  };

  const [searchQueryParams, setSearchQueryParams] = useState({});
  const searchQueryParamsRef = useRef(searchQueryParams);
  const setSearchQueryParamsRef = (data) => {
    searchQueryParamsRef.current = data;
    setSearchQueryParams(data);
  };

  const [areFiltersCleared, setAreFiltersCleared] = useState(false);

  // Helper to wait for user to be set (for addUserDetails to complete on first load)
  const USER_READY_TIMEOUT_MS = 5000;
  const waitForUserReady = async (timeoutMs = USER_READY_TIMEOUT_MS) => {
    const startTime = Date.now();
    while (Date.now() - startTime < timeoutMs) {
      const user = bake({ key: 'user' });
      if (user?.oid || user?.OID) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    console.warn('User not ready after waiting 5s');
    return false;
  };

  const menuCol = {
    id: 'Menu',
    value: null,
    label: 'Actions',
    align: 'center',
    isFixed: true,
  };

  const groupColumns = [
    {
      id: 'EntityMetaType',
      value: -1,
      label: 'Field',
      //align: "center",
    },
    {
      id: 'EntityMetaValue',
      value: -2,
      label: 'Value',
      //align: "center",
    },
    {
      id: 'Available',
      value: -3,
      label: 'Available',
      //align: "center",
    },
    {
      id: 'Reserved',
      value: -4,
      label: 'Reserved',
      //align: "center",
    },
    {
      id: 'Total',
      value: -6,
      label: 'Total',
      //align: "center",
    },
    {
      id: 'EntityOwner',
      value: -5,
      label: 'Owner',
      //align: "center",
    },
  ];

  useEffect(() => {
    setSearchText(bake({ props: props, key: 'searchText' }) ?? '');
    setOrderBy(bake({ props: props, key: 'orderBy' }));
    setOrder(bake({ props: props, key: 'order' }));
    // add 'My' to header title for user homepage
    let pageTitle = props.myHomePage ? 'My ' : '';
    pageTitle +=
      props.level === 'Material'
        ? props.type + 's'
        : props.level
          ? props.level + 's'
          : 'Equipment' + ' ';
    setPageTitle(pageTitle);
    setCurrentColumn(null);
    Api({
      sp: 'getDefaultFields',
      json: {},
    }).then((cols) => {
      let defaultCols = cols
        .filter((x) =>
          props.type
            ? x.Level === (props.level === '' ? 'Equipment' : props.level) &&
              x.EntityType === props.type
            : x.Level === (props.level === '' ? 'Equipment' : props.level)
        )
        .map((x, index) => ({
          id: x.EntityMetaType,
          value: x.EntityMetaType_DBID,
          label: x.EntityMetaType,
          dataType: x.DataType, // add dataType for each column
          sortOrder: index + 1,
        }));
      setDefaultColumns(defaultCols);

      Api({
        sp: 'getEntityTypes',
        json: {},
      }).then((response) => {
        const type = props.type
          ? response.filter((x) => x.EntityType === props.type)[0]
              .EntityType_DBID
          : null;
        setCurrentEntityType(type);
        const sfid = searchParams.get('sfid');
        if (!sfid) {
          fetchData(true, type, defaultCols, undefined, true);
        }
      });
    });
  }, [locationState]);

  useEffect(() => {
    handleResize();
  });

  const handleTextChange = (event) => {
    // Save search text to cookies
    bake({ props: props, key: 'searchText', value: event.target.value });
    setSearchText(event.target.value);
    setPage(0);

    setSearchQueryParamsRef({ text: event.target.value, page: 0 });
  };

  const handleClickOutside = (event) => {
    if (
      ![...event.target.classList].some((x) => x.includes('row')) &&
      ![...event.srcElement.classList].some((x) => x.includes('row')) &&
      // Handle click on nested path element within the Actions menu svg
      !(event.target.tagName === 'path') &&
      // do not clear selection if menu is open or modals are open
      !event.target.dataset.modal &&
      !currentModalRef.current &&
      !event.target.classList.contains('MuiMenuItem-root')
    ) {
      setSelectedRows([]);
      setAnchorSelection(undefined);
    }
  };

  const updateSearchParams = (data, load = false) => {
    let searchJSON = searchQueryParamsRef.current;
    searchJSON = { ...searchJSON, ...data };

    data.filters?.forEach((filter) => {
      let newCssClass = { ...filterCSSClass };
      newCssClass[filter.key] = 'filter-item-updated';
      setFilterCSSClass(newCssClass);
    });
    // if filters are cleared, fetch fresh data and reset filters
    if (data.filters != undefined && data.filters?.length === 0) {
      load = true;
    }
    setSearchQueryParamsRef(searchJSON);
    setFilterSelectedRef(data.filters ?? filterSelectedRef.current);
    if (load || props.myHomePage) {
      getSearchResults(searchJSON);
    }
  };

  const getSearchResults = (data) => {
    setSearchQueryParamsRef(data ?? searchQueryParamsRef.current);
    setFilterCSSClass({});
    getPage(data ?? searchQueryParamsRef.current);
  };

  const keyDown = () => {};

  const fetchData = (
    initial,
    currentType,
    defaultCols,
    enableArchive,
    load = false
  ) => {
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('keydown', keyDown, true);
    let manager =
      (['Material'].includes(props.level) ? 'Material' : 'Equipment') +
      ' Manager';

    setGroupId(
      bake({ props: props, key: 'groupId' })
        ? (Number(bake({ props: props, key: 'groupId' })) ?? -1)
        : -1
    );
    setGroupLocationFilter(
      bake({ props: props, key: 'groupLocationFilter' })
        ? (Number(bake({ props: props, key: 'groupLocationFilter' })) ?? -1)
        : -1
    );
    setRequestOwnerId(
      bake({ props: props, key: 'requestOwnerId' })
        ? (Number(bake({ props: props, key: 'requestOwnerId' })) ?? -1)
        : -1
    );

    let canEdit =
      props.level === 'Lab'
        ? IsRole([
            'Material Manager',
            'Equipment Manager',
            'Admin',
            'Planner',
            'Material Storage',
          ])
        : props.level === 'Location'
          ? IsRole(['Admin', 'Equipment Manager'])
          : ['Material'].includes(props.level)
            ? IsRole(['Material Manager', 'Planner', 'Admin'])
            : IsRole(['ES', manager, 'Admin']);

    setCanEdit(canEdit);

    // get levels for page filtering
    Api({
      sp: 'getLevels',
      json: { level: props.level },
    }).then((response) => {
      setLevels(response);

      // Get data based on view, page, result limit, and filters
      let searchJSON = {
        initial: initial,
        levels: response,
        orderBy: orderBy,
        desc: order === 'desc' ? 1 : 0,
        text: bake({ props: props, key: 'searchText' }) ?? '',
        type: currentType,
        cols: bake({ props: props, key: 'fields' }) ?? defaultCols,
        archiveMode: enableArchive,
        page: initial ? 0 : page,
      };
      if (initial) {
        setPage(0);
      }
      setSearchQueryParamsRef(searchJSON);
      if (load) {
        getSearchResults(searchJSON);
      }
    });

    handleResize();
  };
  // bulk edit after data has been verified and processed
  const bulkEdit = (result) => {
    setSpinner(true);
    Api({
      sp: 'entitiesBulkUpdate',
      json: {
        finalData: result.finalJSON,
        keys: result.keys,
        isName: result.isName,
        isLocation: result.isLocation,
      },
    })
      .then((response) => {
        if (response?.length) {
          toast.success(
            response[0]['TotalRows'] + ' entries successfully updated!'
          );
        }
        if (result.sapNumberUpate?.length > 0) {
          downloadAssetNumberUpdateReport(result.sapNumberUpate);
        }
        closeModal();
        fetchData(true);
        setSpinner(false);
      })
      .catch((e) => {
        console.log('Error ' + e);
        setSpinner(false);
        toast.error('An error occured while trying to process your request.');
      });
  };

  const bulkUpload = (
    finalData,
    keys,
    query,
    parentId,
    typeId,
    fileName,
    parentEntityList,
    entityType,
    entityStatus,
    giveComment
  ) => {
    setSpinner(true);
    Api({
      sp: 'bulkUpload',
      json: {
        finalData: finalData,
        keys: keys,
        query: query,
        parentId: parentId,
        typeId: typeId,
        matFlag: props.level === 'Material' ? 1 : 0,
        ssdType: entityType === 'SSD' ? 1 : 0,
        locFlag: props.level === 'Location' ? 1 : 0,
        status: entityStatus,
        giveComment: giveComment,
      },
    })
      .then((response) => {
        let uploadedRows = response.filter((res) => res.Dup_Row === 0);
        let dups = response.filter((res) => res.Dup_Row === 1);

        if (uploadedRows.length > 0) {
          // Add new entities to entityLineage container in Cosmos
          if (['Component'].includes(entityType)) {
            // Transform response to match entityLineage container format
            const documentArr = response.map((obj) => {
              const {
                Entity_DBID: entityDBID,
                LotQuantity: lotQuantity,
                Transaction_DBID: transactionDBID,
              } = obj;

              return {
                entityDBID,
                lotQuantity,
                parents: [],
                createdDate: new Date(),
                pk: entityType.toLowerCase(),
                ...(transactionDBID && { transactionDBID }), // Only add transactionDBID property if part of Bulk Upload Give It
              };
            });

            Cosmos({
              cosmosApi: 'addDocuments',
              containerId: 'entityLineage',
              documentArr: documentArr,
            });
          }

          // Set Give Its
          let giveIts = {};
          if (uploadedRows[0].Transaction_DBID) {
            uploadedRows.map((row) => {
              let giveId = row.Transaction_DBID;
              if (giveId in giveIts) {
                giveIts[giveId].push(row.Entity_DBID);
              } else {
                giveIts[giveId] = [];
                giveIts[giveId].push(row.Entity_DBID);
              }
            });
            setGiveIts(giveIts);
          }

          setUploaded(uploadedRows);

          setSpinner(false);

          fetchData(true);
        } else {
          setDuplicates(dups);
          setSpinner(false);
        }
      })
      .catch((e) => {
        console.log('Error ' + e);
        setSpinner(false);
        toast.error('An error occured while trying to process your request.');
      });
  };

  const getPage = (data) => {
    let cols =
      data.cols ?? bake({ props: props, key: 'fields' }) ?? defaultColumns;
    cols = cols.map((col, index) => ({ ...col, sortOrder: index + 1 }));
    setSortedColumns(cols);
    setFilteredRows([]);
    if (!data.paging) {
      setTotalRows(0);
    }
    setLoading(true);
    const json = getQueryJSON(
      data.levels ?? levels,
      data,
      cols,
      data.type ?? currentEntityType,
      data.archiveMode ?? archiveMode
    );
    let group = data.group ?? Number(bake({ props: props, key: 'groupId' }));
    cols = !group || group === -1 ? cols : groupColumns;

    cols = cols.filter((x) => x.id !== 'Menu');

    cols.push(menuCol);
    setColumns(cols);

    if (!group || group === -1) {
      // do not call searchQuery for initial load if meta is not present to avoid empty call
      if (
        (json?.['meta']?.length && json?.['meta'] != undefined) ||
        cols?.length > 0
      ) {
        setGroupedColumns([]);
        setFilteredRows([]);

        // Wait for user to be ready before calling searchQuery (ensures addUserDetails is complete on first load)
        // Avoids unnecessary retries in searchQuery when user details are not yet available
        waitForUserReady().then((userReady) => {
          if (!userReady) {
            setLoading(false);
            toast.error(
              'Session initialization timed out. Please re-authenticate and try again.'
            );
            return;
          }

          Api({
            sp: 'searchQuery',
            json: json,
          }).then((response) => {
            // Pop row count from the response and use it to set total rows
            let rowCount = response?.[0]?.['Row Count'] ?? 0;
            if (!response || rowCount === 0) {
              setLoading(false);
              toast.info(
                'No Records found. Check column filters and site-wide filter settings'
              );
              return;
            }
            setTotalRows(+rowCount);
            let rows = [];
            let metaHeaders = Object.keys(response?.[0] ?? {});
            response.forEach((r) => {
              let newRow = {};
              // Map each column to its corresponding entity metadata
              // and format the data based on its type
              // update certain columns for column header
              let entityMetaType = undefined;
              cols.forEach((col) => {
                switch (col.id) {
                  case 'Type':
                    entityMetaType = 'EntityType';
                    break;
                  case 'Name':
                    entityMetaType = 'EntityName';
                    break;
                  case 'Owner':
                    entityMetaType = 'EntityOwnerName';
                    break;
                  case 'Status':
                    entityMetaType = 'EntityStatus';
                    break;
                  default:
                    entityMetaType = col.id;
                    break;
                }
                // change binary to display as boolean string
                let entityMetaValue = undefined;
                switch (col.DataType) {
                  case 'Binary':
                    entityMetaValue = r[entityMetaType] === '1' ? 'True' : null;
                    break;
                  case 'Date':
                    entityMetaValue = datePickerToShortDate(r[entityMetaType]); // update date to short date format
                    break;
                  default:
                    entityMetaValue = r[entityMetaType];
                    break;
                }
                if (col.id !== 'Menu') {
                  newRow = {
                    ...newRow,
                    [entityMetaType]: entityMetaValue?.toString()?.split('|'), // split by pipe for multiple values, e.g., Notes
                  };
                }
              });
              // retain the necessary properties for the row
              newRow = {
                ...newRow,
                Entity_DBID: r.Entity_DBID,
                EntityName: r.EntityName,
                Name: r.EntityName,
                Type: r.EntityType,
                Status: r.EntityStatus,
                EntityStatus: r.EntityStatus,
                EntityType: r.EntityType,
                Owner: r.EntityOwnerName,
                EntityOwner: r.EntityOwner,
                EntityOwnerName: r.EntityOwnerName,
                ArchivedBy: r.ArchivedBy,
                ArchiveDate: r.ArchiveDate,
                EntityType_DBID: r.EntityType_DBID,
                Path: r.Location?.trim(),
                Location: r.Location?.split('>'),
              };

              // Add additional metadata columns that are not in the main columns
              metaHeaders
                .filter(
                  (metaType) =>
                    !Object.keys(newRow).includes(metaType) &&
                    metaType !== 'Row Count'
                )
                .forEach((metaType) => {
                  newRow[metaType] = r[metaType];
                });
              rows.push(newRow);
            });
            setFilteredRows(rows);
            setSelectedRows(
              selectedRows.filter((x) =>
                rows.some((y) => x.Entity_DBID === y.Entity_DBID)
              )
            );
            setLoading(false);
          });
        });
      } else {
        setLoading(false);
      }
    } else {
      setGroupedColumns(cols);
      let payload = getGroupPayload(data, group, cols);
      Api({
        sp: 'getPageByGroup',
        json: payload,
      }).then((response) => {
        setTotalRows(response.length ? response[0].TotalRows : 0);
        response.forEach((x) => {
          x.Available = x.Total - x.Reserved ?? 0;
          x.Reserved = x.Reserved ?? 0;
        });
        let rowsTemp = response.filter((x) => x.EntityMetaType !== 'Total');
        setFilteredRows(rowsTemp);
        setSelectedRows(
          selectedRows.filter((x) =>
            rowsTemp.some((y) => x.EntityMetaValue === y.EntityMetaValue)
          )
        );
        setLoading(false);
      });
    }
  };

  const getGroupPayload = (data, group, cols) => {
    let rpp = parseInt(bake({ props: props, key: 'rowsPerPage' }), 10);
    rpp = isNaN(rpp) ? 15 : rpp;

    setFilterSelectedRef(
      data.filters ?? bake({ props: props, key: 'filters' })
    );

    return {
      level: data.level ?? props.level,
      entityType: props.type,
      page: data.page ?? page,
      rows: data.rows ?? rpp,
      text: data.text ?? searchText,
      orderBy: data.orderBy ?? orderBy,
      desc: (data.desc ?? order === 'desc') ? 1 : 0,
      cols: cols,
      filters: data.filters ?? bake({ props: props, key: 'filters' }),
      group: group,
      groupLocationFilter:
        data.groupLocationFilter ??
        Number(bake({ props: props, key: 'groupLocationFilter' })),
      owner:
        data.requestOwnerId ??
        Number(bake({ props: props, key: 'requestOwnerId' })),
    };
  };

  const refreshReserved = (data) => {
    let rows = filteredRows;
    rows.forEach((r) => {
      data.reserved.forEach((d) => {
        if (r.EntityMetaValue === d.EntityMetaValue) {
          r.Available -= d.reserve;
          r.Reserved += parseInt(d.reserve);
        }
      });
    });
  };

  const closeRequestModal = () => {
    setCurrentModalRef(null);
    setCartActive(false);

    setSearchQueryParams({
      group: bake({ props: props, key: 'groupId' }),
      groupLocationFilter: bake({ props: props, key: 'groupLocationFilter' }),
      requestOwnerId: bake({ props: props, key: 'requestOwnerId' }),
    });
  };

  const updateRequest = (data) => {
    setRequest(data);
  };

  const closeModal = (edit) => {
    //turn off spinner when closing modal
    setSpinner(false);
    if (edit) {
      setCurrentRow(edit);
      setCurrentModalRef('EntityEdit');
      setActionModalOpen(true);
    } else {
      setCurrentModalRef(null);
      if (!groupId || groupId === -1) {
        getPage({});
      }
    }
  };

  const handleClick = (e, row) => {
    switch (e.detail) {
      case 2:
        setCurrentRow(row);
        if (
          (!groupId || groupId === -1) &&
          !archiveMode &&
          currentModalRef !== 'EntityEdit'
        ) {
          setCurrentModalRef('EntityEdit');
          setActionModalOpen(true);
        }
        break;
      default:
        toggleSelected(row, e);
        return;
    }
  };

  const onFilterClick = (key) => {
    setCurrentModalRef('ColumnFilter');
    setCurrentColumn(columns.find((c) => c.id === key));
  };

  const toggleSelected = (row, event) => {
    let rows = selectedRows;
    // sort rows array according to table sort
    let property = !groupedColumns.length ? 'Entity_DBID' : 'EntityMetaValue';
    if (rows.length) {
      // if control key is pressed, toggle current row selection without affecting other selected rows
      if (event.ctrlKey && !archiveMode) {
        if (rows.some((x) => x[property] === row[property])) {
          setSelectedRows(rows.filter((x) => x[property] !== row[property]));
        } else {
          rows.push(row);
          setCurrentRowHover(null);
          setSelectedRows(rows);
        }
      } else if (event.shiftKey && !archiveMode) {
        // get index of anchor row
        const anchorRow = filteredRows.findIndex(
          (o2) => o2[property] === anchorSelection[property]
        );
        // get index of current row
        const currentRow = filteredRows.findIndex(
          (o2) => o2[property] === row[property]
        );
        const firstSelectedRow =
          currentRow < anchorRow ? currentRow : anchorRow;
        const lastSelectedRow = currentRow > anchorRow ? currentRow : anchorRow;
        let newRows = [];
        // select all rows from first to last
        filteredRows.forEach((r, i) => {
          if (i >= firstSelectedRow && i <= lastSelectedRow) {
            newRows.push(r);
          }
        });
        setSelectedRows(newRows);
      } else {
        // if control key is not pressed and no rows are selected, select current row
        setSelectedRows([row]);
        setAnchorSelection(row);
      }
    } else {
      // toggle current row selection and clear any other selected rows
      setSelectedRows([row]);
      setAnchorSelection(row);
    }
  };

  const setRowHover = (row) => {
    // NOTE: This function causes the entire component to re-render on every hover
    if (!currentModal) {
      setCurrentRowHover(row);
    }
  };

  const clearRowHover = () => {
    // NOTE: This function causes the entire component to re-render on every hover
    if (!currentModal) {
      setCurrentRowHover(null);
    }
  };

  const isSelected = (row) => {
    let property = !groupedColumns.length ? 'Entity_DBID' : 'EntityMetaValue';
    return selectedRows.some((x) => x[property] === row[property]);
  };

  const handleContextClick = (event, row) => {
    setCurrentRow(row);
    let property = !groupedColumns.length ? 'Entity_DBID' : 'EntityMetaValue';
    if (!selectedRows.some((x) => x[property] === row[property])) {
      setSelectedRows([]);
    }
    event.stopPropagation();

    setActionContextAnchor(event.currentTarget);
    setContextRow(currentRowHover);
  };

  const handleActionContextClose = (refresh, modal) => {
    setActionModalOpen(modal ? true : false);
    setCurrentModalRef(modal);
    setActionContextAnchor(null);
    setContextRow(null);
    if (refresh) {
      setSearchQueryParams({});
    }
  };

  // utlizes isoUtil.js to fetch ISO fields and data for the current equipment
  const handlePerformService = async () => {
    let isoFieldOptions = await fetchIsoFields();
    let maintenanceMap = await fetchIsoData([currentRow]);
    setISOData(maintenanceMap);
    setISOFields(isoFieldOptions);
  };

  const openServiceHistory = async () => {
    let isoFieldOptions = await fetchIsoFields();
    let maintenanceMap = await fetchIsoData([currentRow]);
    setISOData(maintenanceMap);
    setISOFields(isoFieldOptions);
  };

  const setGroup = (e) => {
    // Save groupId, groupLocationFilter and owner to cookies
    bake({ props: props, key: 'groupId', value: e.EntityMetaType_DBID });
    bake({
      props: props,
      key: 'groupLocationFilter',
      value: e.GroupLocationFilter,
    });
    bake({ props: props, key: 'requestOwnerId', value: e.User_DBID });
    setGroupId(e.EntityMetaType_DBID);
    setGroupLocationFilter(e.GroupLocationFilter);
    setRequestOwnerId(e.User_DBID);
    setCurrentModalRef(null);
    setSelectedRows([]);
    setCurrentRow(null);
    setRequest({ reserved: [] });
    getPage({
      group: e.EntityMetaType_DBID,
      groupLocationFilter: e.GroupLocationFilter,
      requestOwnerId: e.User_DBID,
    });
  };

  const updateColumns = (cols) => {
    cols = cols.map((x) => ({
      id: x.EntityMetaType,
      value: x.EntityMetaType_DBID,
      label: x.EntityMetaType,
      dataType: x.DataType,
    }));
    cols = [...cols.filter((x) => x.id !== 'Menu')];
    cols = cols.map((col, index) => ({ ...col, sortOrder: index + 1 }));
    setSortedColumns(cols);
    cols.push(menuCol);
    setColumns(cols);
    bake({
      props: props,
      key: 'fields',
      value: cols.filter((x) => x.id !== 'Menu'),
    });
    let trimmedFilters = bake({ props: props, key: 'filters' })?.filter((f) =>
      cols.some((c) => c.id === f.key)
    );
    setFilterSelectedRef(trimmedFilters);
    bake({ props: props, key: 'filters', value: trimmedFilters });
    let localOrderBy = cols.some((x) => x.id === orderBy)
      ? orderBy
      : cols[0].id;
    setOrderBy(localOrderBy);
    bake({ props: props, key: 'orderBy', value: localOrderBy });
  };

  const updateGroups = () => {
    setLoading(false);
    setFilteredRows([]);
    let cols = groupedColumns;
    cols = cols.filter((x) => x.label !== currentColumn.label);
    if (cols.length === groupedColumns.length) {
      cols.push(currentColumn);
    }
    setGroupedColumns(cols);
    setHeaderContextAnchor(null);
    if (!cols.length) {
      setColumns(columns.filter((x) => x.id !== 'Total'));
      getPage({
        cols: columns.filter((x) => x.id !== 'Total'),
      });
      return;
    }

    // Save selected sort column & order to cookies
    bake({ props: props, key: 'orderBy', value: currentColumn.id });
    bake({ props: props, key: 'order', value: 'asc' });
    setOrder('asc');
    setOrderBy(currentColumn.id);
    setPage(0);

    getGroupedColumnData(cols, currentColumn.id, 'asc');
  };

  const getGroupedColumnData = (cols, orderBy, order) => {
    const body = BuildGroupQuery(cols, props.level, orderBy, order);
    Query({ query: body }).then((response) => {
      let cols = columns.filter((x) => x.id !== 'Total');
      cols.splice(cols.length - 1, 0, {
        id: 'Total',
        value: null,
        label: 'Total',
      });
      setFilteredRows(response);
      setColumns(cols);
      setLoading(false);
    });
  };

  const getQueryJSON = (levels, data, cols, currentType, archiveMode) => {
    let levelIds =
      props.level === ''
        ? [2, 3, 4]
        : [levels.filter((x) => x.Level === props.level)[0]?.Level_DBID];

    let invalidEntityLevelIds =
      props.level === 'Material'
        ? levels.filter((x) => x.Level === 'EPO').map((a) => a.Level_DBID)
        : [];

    // set whether these columns exist in order to search their values
    let searchEntityName = cols.some(
      (x) => x.id === 'EntityName' || x.id === 'Name'
    );
    let searchArchivedBy = cols.some((x) => x.id === 'ArchivedBy');
    let searchArchiveDate = cols.some((x) => x.id === 'ArchiveDate');

    let searchEntityType = cols.some(
      (x) => x.id === 'EntityType' || x.id === 'Type'
    );
    let searchLocation = cols.some((x) => x.id === 'Location');
    let searchMeta = cols.some((x) => x.value !== null);

    let meta = [];
    // fetch orderBy from localStorage.
    // if not found, check if there's sorting on the page locally
    let localOrderBy =
      data.orderBy ??
      bake({ props: props, key: 'orderBy' }) ??
      orderBy ??
      cols[0].id;
    let exactEntityTypeIds = [];
    let exactEntityNames = [];
    let exactArchivedBy = [];
    let exactArchiveDate = [];
    let orderById = -1;
    let filters = data.filters ?? bake({ props: props, key: 'filters' });
    setFilterSelectedRef(filters);
    let metaDict = {};
    let searchExactMeta = false;
    let exactMetaCount = -1;
    let exactLocations = [];
    let metaIds = '';
    // set order
    if (
      !['Name', 'Type', 'Location', 'ArchiveDate', 'ArchivedBy'].includes(
        localOrderBy
      )
    ) {
      orderById = cols.filter((x) => x.id === localOrderBy)[0]?.value;
      localOrderBy = 'Meta';
    } else {
      localOrderBy =
        localOrderBy === 'Location' ? localOrderBy : 'Entity' + localOrderBy;
    }

    // get columns
    cols.forEach((col) => {
      if (col.value && !(col.value in metaDict)) {
        metaDict[col.value] = [];
      }
    });

    // get filters
    if (filters) {
      filters.forEach((filter) => {
        if (filter.key === 'Location') {
          exactLocations.push(filter.value);
        } else if (filter.key === 'Type') {
          exactEntityTypeIds.push(filter.DBID);
        } else if (filter.key === 'Name') {
          exactEntityNames.push(filter.value);
        } else if (filter.key === 'ArchivedBy') {
          exactArchivedBy.push(filter.value);
        } else if (filter.key === 'ArchiveDate') {
          exactArchiveDate.push(filter.value);
        } else {
          searchExactMeta = true;
          if (!(filter.DBID in metaDict)) {
            metaDict[filter.DBID] = [filter.value];
          } else {
            if (metaDict[filter.DBID].indexOf(filter.value) === -1) {
              metaDict[filter.DBID].push(filter.value);
            }
          }
        }
      });
    }

    for (const [key, value] of Object.entries(metaDict)) {
      searchMeta = true;
      let metaObj = {};
      metaObj['metaId'] = parseInt(key);
      if (value !== undefined && value.length !== 0) {
        exactMetaCount += 1;
      }
      metaObj['exactValues'] = value;
      if (metaObj['metaId'] > 0) {
        meta.push(metaObj);
      }
    }

    // special rules for materials that are already filtered by type
    searchEntityType = props.type ? true : searchEntityType;
    exactEntityTypeIds = props.type ? [currentType] : exactEntityTypeIds;

    if (data.export) {
      metaIds = meta.map((m) => m.metaId);
    }

    return {
      searchTerm: data.text ?? searchText,
      searchEntityName: searchEntityName,
      exactEntityNames: exactEntityNames,
      searchArchivedBy: searchArchivedBy,
      exactArchivedBy: exactArchivedBy,
      searchArchiveDate: searchArchiveDate,
      exactArchiveDate: exactArchiveDate,
      searchEntityType: searchEntityType,
      exactEntityTypeIds: exactEntityTypeIds,
      searchLocation: searchLocation,
      exactLocations: exactLocations,
      meta: meta,
      searchExactMeta: searchExactMeta,
      exactMetaCount: exactMetaCount,
      searchMeta: searchMeta,
      orderBy: localOrderBy,
      orderById: orderById,
      orderByDirection:
        data.order ?? bake({ props: props, key: 'order' }) ?? 'asc',
      page: data.page ?? page,
      // if rowsPerPage from localStorage is NaN, default to 15
      limit:
        data.rows ??
        (parseInt(bake({ props: props, key: 'rowsPerPage' }), 10) || 15),
      levelIds: levelIds,
      invalidEntityLevelIds: invalidEntityLevelIds,
      metaIds: metaIds,
      locationFilter: bake({ key: 'locationFilter' }),
      archiveMode: archiveMode,
      // identify if the user is viewing their own homepage
      userOwnedFilter: props.myHomePage,
    };
  };

  const isHovered = (row) => {
    const hovered =
      (currentRowHover?.Entity_DBID === row.Entity_DBID &&
        !groupedColumns.length) ||
      (groupedColumns.length &&
        currentRowHover?.EntityMetaValue === row.EntityMetaValue);
    return hovered;
  };

  const enableBulkUpload = () => {
    setCurrentModalRef('EntityBulkUpload');
    setUploaded([]);
    setDuplicates([]);
    setGiveIts(null);
  };

  const enableBulkEdit = () => {
    setCurrentModalRef('EntityBulkEdit');
  };

  const fetchArchivalView = (archiveFlag) => {
    setArchiveMode(archiveFlag);
    fetchData(true, undefined, undefined, archiveFlag, true);
  };

  const clearAllFilters = () => {
    // Clear searchText state and cookies
    setSearchText('');
    bake({ props: props, key: 'searchText', value: '' });

    bake({ props: props, key: 'filters', value: [] });

    setFilterSelectedRef([]);
    setAreFiltersCleared(true);
    setSelectedRows([]);
    setPage(0);

    // Refresh the page with cleared filters
    setSearchQueryParams({ text: '', page: 0 });
    getSearchResults({ text: '', page: 0 });
  };

  const closeColumnFilter = (selected, column) => {
    let filters = bake({ props: props, key: 'filters' });
    let prevFilters = filters ? filters.filter((x) => x.key === column.id) : [];
    filters = filters ? filters.filter((x) => x.key !== column.id) : [];
    let colFilter = [];
    selected.forEach((s) => {
      colFilter.push({
        key: column.id,
        field: column.id,
        label: s.EntityMetaValue,
        value: s.EntityMetaValue,
        DBID: s.DBID ?? column.value,
      });
    });

    // Check if newFilters is different than colFilter and update CSS class
    if (JSON.stringify(prevFilters) !== JSON.stringify(colFilter)) {
      let newCssClass = { ...filterCSSClass };
      newCssClass[column.id] = 'filter-item-updated';
      setFilterCSSClass(newCssClass);
    }

    let newFilters = filters.concat(colFilter);
    setFilterSelectedRef(newFilters);
    setCurrentModalRef(null);
    // if no filter changes, do nothing
    if (
      JSON.stringify(bake({ props: props, key: 'filters' })) ===
      JSON.stringify(newFilters)
    ) {
      return;
    }
    bake({ props: props, key: 'filters', value: newFilters });
    // load search results if the last filter is removed
    const pageLoad = newFilters.length === 0;
    updateSearchParams({ filters: newFilters }, pageLoad);
  };

  return (
    <div className="main-container">
      {/* Page Title */}
      <PageHeader
        level={props.level}
        type={props.type}
        pageTitle={pageTitle}
        columns={columns}
        groupId={groupId}
        request={request}
        searchText={searchText}
        canEdit={canEdit}
        archiveMode={archiveMode}
        currentModal={currentModal}
        currentEntityType={currentEntityType}
        areFiltersCleared={areFiltersCleared}
        fetchData={fetchData}
        getSearchResults={(e) => getSearchResults(e)}
        setHeaderContextAnchor={(e) => setHeaderContextAnchor(e)}
        setCurrentModalRef={(e) => setCurrentModalRef(e)}
        handleTextChange={(event) => handleTextChange(event)}
        enableBulkUpload={() => enableBulkUpload()}
        enableBulkEdit={() => enableBulkEdit()}
        fetchArchivalView={(e) => fetchArchivalView(e)}
        setCartActive={(e) => setCartActive(e)}
        closeModal={(e) => closeModal(e)}
        // disable button groups and filter information on homepage
        noHeaderContext={props.myHomePage}
        clearAllFilters={clearAllFilters}
      ></PageHeader>

      {filterSelectedRef.current?.length > 0 || searchText?.length > 0 ? (
        <FiltersSelected
          filters={filterSelectedRef.current}
          onFilterClick={(k) => onFilterClick(k)}
          sortedColumns={sortedColumns}
          getSearchResults={(e) => getSearchResults(e)}
          filterCSSClass={filterCSSClass}
          clearAllFilters={clearAllFilters}
        />
      ) : null}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <PageTable
          columns={columns}
          filteredRows={filteredRows}
          groupColumns={groupColumns}
          groupedColumns={groupedColumns}
          headerContextAnchor={headerContextAnchor}
          archiveMode={archiveMode}
          level={props.level}
          myHomePage={props.myHomePage}
          currentModal={currentModal}
          grouped={
            groupId && groupId !== -1
              ? (groupId && groupId !== -1).toString()
              : 'false'
          }
          loading={loading}
          type={props.type}
          currentColumn={currentColumn}
          updateColumns={(e) => updateColumns(e)}
          updateGroups={(e) => updateGroups(e)}
          updateSearchParams={(e, load) => updateSearchParams(e, load)}
          setPage={(e) => setPage(e)}
          setCurrentModalRef={(e) => setCurrentModalRef(e)}
          handleClick={(e, r) => handleClick(e, r)}
          setRowHover={(r) => setRowHover(r)}
          clearRowHover={(e) => clearRowHover(e)}
          isHovered={(r) => isHovered(r)}
          isSelected={(r) => isSelected(r)}
          handleContextClick={(e, r) => handleContextClick(e, r)}
          setSortedColumns={(e) => setSortedColumns(e)}
        />

        <PageFooter
          selectedRows={selectedRows}
          totalRows={totalRows}
          level={props.level}
          type={props.type}
          myHomePage={props.myHomePage}
          page={page}
          updateSearchParams={(e, load) => updateSearchParams(e, load)}
          setPage={(e) => setPage(e)}
        />
      </Paper>
      {currentModal === 'EntityCreate' ? (
        <EntityCreate
          level={props.level}
          type={props.type}
          srcLevel={props.level}
          creating={currentModal === 'EntityCreate'}
          closeModal={closeModal}
          epoSource={currentRow}
          epoSources={selectedRows.length > 0 ? selectedRows : [currentRow]}
        ></EntityCreate>
      ) : null}

      {currentModal === 'EntityBulkUpload' ? (
        <EntityBulkUpload
          level={props.level}
          type={props.type}
          rows={filteredRows}
          createBulk={currentModal === 'EntityBulkUpload'}
          closeModal={closeModal}
          saveData={bulkUpload}
          savedData={uploaded}
          giveIts={giveIts}
          duplicates={duplicates}
          spinner={spinner}
        ></EntityBulkUpload>
      ) : null}

      {currentModal === 'EntityBulkEdit' ? (
        <EntityBulkEdit
          level={props.level}
          type={props.type}
          createBulk={currentModal === 'EntityBulkEdit'}
          closeModal={closeModal}
          saveData={bulkEdit}
          spinner={spinner}
          canEdit={canEdit}
        ></EntityBulkEdit>
      ) : null}

      {currentModal === 'ExportCSV' ? (
        <ExportCSV
          csvData={filteredRows}
          fileName={
            (props.level ? props.level : 'Equipment') +
            (props.type ? '_' + props.type : '') +
            '_' +
            getDateTime()
          }
          columns={columns}
          order={order}
          orderBy={orderBy}
          exportCSV={currentModal === 'ExportCSV'}
          closeExport={closeModal}
          getGroupPayload={getGroupPayload}
          groupColumns={groupColumns}
          groupId={groupId}
          levels={levels}
          level={props.level}
          getQueryJSON={getQueryJSON}
          totalRows={totalRows}
          entityType={currentEntityType}
        />
      ) : null}

      {currentModal === 'GroupedView' ? (
        <GroupSelect
          grouping={currentModal === 'GroupedView'}
          closeModal={closeModal}
          update={setGroup}
          fieldId={Number(groupId)}
          locationId={Number(groupLocationFilter)}
          ownerId={Number(requestOwnerId)}
          matType={props.type}
        />
      ) : null}
      {currentModal === 'RequestCreate' ? (
        <TransactionRequestCreate
          requesting={currentModal === 'RequestCreate'}
          closeModal={closeRequestModal}
          update={updateRequest}
          request={request}
          refresh={refreshReserved}
          location={Number(groupLocationFilter)}
          entityOwner={Number(requestOwnerId)}
          isClassStoreMember={IsRole('Material Storage')}
          cartActive={cartActive}
          matType={props.type}
          items={
            selectedRows.length > 0
              ? selectedRows.filter((x) => x.Available > 0)
              : [currentRow]
          }
        />
      ) : null}
      {currentModal === 'EntityLocationScan' ? (
        <EntityLocationScan
          open={currentModal === 'EntityLocationScan'}
          close={closeModal}
          scanMode={true}
          // managers and planners can confirm location for the drives owned by other users
          canEdit={IsRole(['Material Manager', 'Planner', 'Admin'])}
        />
      ) : null}
      {currentModal === 'EquipmentService' ? (
        <EquipmentService
          show={currentModal === 'EquipmentService'}
          isoData={isoData?.[currentRow?.Entity_DBID]}
          ISOFields={ISOFields}
          entity={currentRow}
          closeModal={(e) => closeModal(e)}
        />
      ) : null}

      {currentModal === 'ColumnFilter' ? (
        <ColumnFilter
          close={(selected, column) => closeColumnFilter(selected, column)}
          open={currentModal === 'ColumnFilter'}
          level={props.level}
          column={currentColumn}
          selected={filterSelectedRef.current.filter(
            (f) => f.key === currentColumn?.id
          )}
          type={props.type}
        />
      ) : null}

      {currentModal === 'EquipmentServiceHistory' ? (
        <EquipmentServiceHistory
          show={currentModal === 'EquipmentServiceHistory'}
          isoData={isoData?.[currentRow?.Entity_DBID]}
          ISOFields={ISOFields}
          entity={currentRow}
          closeModal={(e) => closeModal(e)}
        />
      ) : null}

      {currentModal === 'EquipmentService' ? (
        <EquipmentService
          show={currentModal === 'EquipmentService'}
          isoData={isoData?.[currentRow?.Entity_DBID]}
          ISOFields={ISOFields}
          entity={currentRow}
          closeModal={(e) => closeModal(e)}
        />
      ) : null}
      {currentModal === 'EquipmentServiceHistory' ? (
        <EquipmentServiceHistory
          show={currentModal === 'EquipmentServiceHistory'}
          isoData={isoData?.[currentRow?.Entity_DBID]}
          ISOFields={ISOFields}
          entity={currentRow}
          closeModal={(e) => closeModal(e)}
        />
      ) : null}
      {actionContextAnchor || actionModalOpen ? (
        <ActionContext
          level={props.level}
          canEdit={canEdit}
          isClassStoreMember={IsRole('Material Storage')}
          grouped={groupId && groupId !== -1}
          currentRow={currentRow}
          columns={columns}
          contextAnchor={actionContextAnchor}
          handleActionContextClose={handleActionContextClose}
          selectedRows={selectedRows}
          contextRow={contextRow}
          currentModal={currentModal}
          closeModal={closeModal}
          request={request}
          matType={props.type}
          archiveMode={archiveMode}
          myHomePage={props.myHomePage}
          handlePerformService={handlePerformService}
          openServiceHistory={openServiceHistory}
        ></ActionContext>
      ) : null}
    </div>
  );
}
